import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiModal,
  MuiSelect,
  MuiTextField,
} from "../../../../../../components";
import {
  DOCUMENT_SITE_CORE_KEY,
  PAGE_IDS,
} from "../../../../../../helpers/constants";
import {
  getValueOrBlank,
  replaceHashWithValue,
} from "../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import {
  ArchiveContractDocumentFormKey,
  ClientDocument,
} from "../../../../modelsAndSchemas/Models";
import { ArchiveContractDocumentModalValidationSchema } from "../../../../modelsAndSchemas/ValidationSchema";
import { documentSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { ArchiveContractDocumentModalsWrapper } from "./ArchiveContractDocumentModal.style";

export interface ArchiveContractDocumentModalProps {
  open: boolean;
  documentData?: ClientDocument;
  onConfirm: (cancellationReason: string, comment?: string) => void;
  onClose: () => void;
}

const ArchiveContractDocumentModal: FC<ArchiveContractDocumentModalProps> = ({
  documentData,
  open,
  onClose,
  onConfirm,
}) => {
  const { commonARIALabel } = useNavigationSiteCore();
  const {
    fetchLabel,
    commonLabels,
    fetchValidationMessage,
    fetchDropdownValues,
  } = useSiteCore();

  const methods = useForm({
    mode: "onBlur",
    defaultValues: {
      [ArchiveContractDocumentFormKey.archivalReason]: "",
      [ArchiveContractDocumentFormKey.archivalComments]: "",
    },
    resolver: yupResolver(
      ArchiveContractDocumentModalValidationSchema(fetchValidationMessage),
    ),
  });

  const { control, getValues, handleSubmit } = methods;
  const [showCommentField, setShowCommentField] = useState(false);

  const { gridSizeMedium: gridSize } = useGridContainer();
  const pageId = PAGE_IDS.ARCHIVE_CONTRACT_MODAL;

  const getLabel = (key: keyof typeof documentSiteCoreKeys) => {
    return fetchLabel(documentsTitle, documentSiteCoreKeys[key]);
  };
  const { documentsTitle } = documentSiteCoreKeys;

  const getDropdownList = (key: keyof typeof documentSiteCoreKeys) => {
    return fetchDropdownValues(documentsTitle, documentSiteCoreKeys[key]);
  };

  const handleArchiveContractDocumentSubmit = () => {
    const { archivalReason, archivalComments } = getValues();
    onConfirm(archivalReason, archivalComments);
  };

  const onChangeReason = () => {
    const { archivalReason } = getValues();
    setShowCommentField(
      archivalReason === DOCUMENT_SITE_CORE_KEY.documentArchiveReasonOther,
    );
  };

  const getReasonDropdown = () => {
    return getValueOrBlank(documentData?.documentType) === "contract"
      ? getDropdownList("archiveReasonList")
      : getDropdownList("othersArchiveReasonList");
  };

  return (
    <FormProvider {...methods}>
      <MuiModal
        open={open}
        onClose={onClose}
        title={replaceHashWithValue(
          getLabel("archivePopupHeader"),
          documentData?.documentName ?? "",
        )}
        hasLargeContent
        secondaryButtonTitle={commonLabels.cancel}
        primaryButtonTitle={commonLabels.save}
        onSecondaryButtonClick={onClose}
        onPrimaryButtonClick={() =>
          handleSubmit(handleArchiveContractDocumentSubmit)()
        }
      >
        <ArchiveContractDocumentModalsWrapper>
          <MuiBox className="container">
            <MuiGrid container>
              <MuiGrid size={gridSize}>
                <FormController
                  control={control}
                  name={ArchiveContractDocumentFormKey.archivalReason}
                >
                  <MuiSelect
                    id={`${pageId}-${ArchiveContractDocumentFormKey.archivalReason}`}
                    visibleDefaultSelectOption
                    label={getLabel("archiveReasonDropdown")}
                    options={getReasonDropdown()}
                    placeholder={commonLabels.selectPlaceHolder}
                    isMandatory
                    onChange={onChangeReason}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>
            </MuiGrid>
            {showCommentField && (
              <MuiBox className="archive-contract-document-comment">
                <FormController
                  control={control}
                  name={ArchiveContractDocumentFormKey.archivalComments}
                >
                  <MuiTextField
                    placeholder={commonLabels.inputPlaceHolder}
                    label={commonLabels.comment}
                    id={`${pageId}-${ArchiveContractDocumentFormKey.archivalComments}`}
                    multiline
                    className="comment-input"
                    rows={4}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiBox>
            )}
          </MuiBox>
          <MuiBox className="separator" />
        </ArchiveContractDocumentModalsWrapper>
      </MuiModal>
    </FormProvider>
  );
};
export default ArchiveContractDocumentModal;
